import { formatMoney } from "../utils/format";
import Dialog from "./Dialog";
import { discardChanges, useConfirm } from "./ConfirmProvider";
import useUnsavedChanges from "../hooks/useUnsavedChanges";
import React, { useEffect, useId, useRef, useState } from "react";

import { useForm } from "react-hook-form";

const PrestationForm = ({ prestation, onSubmit, onCancel }) => {
  const [submitError, setSubmitError] = useState("");
  const lock = useRef(false);
  const formId = useId();
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting },
    reset,
    watch,
  } = useForm({ defaultValues: { description: "", unite: "heure", quantite: 1, prix_unitaire: 75, devise: "EUR" } });

  const watchedQuantite = watch("quantite", 1);
  const watchedPrixUnitaire = watch("prix_unitaire", 75);

  useEffect(() => {
    if (prestation) {
      reset(prestation);
    }
  }, [prestation, reset]);

  const confirm = useConfirm();
  useUnsavedChanges(isDirty);
  const close = async () => {
    if (isSubmitting) return;
    if (!isDirty || await confirm(discardChanges)) onCancel();
  };

  const handleFormSubmit = async (data) => {
    if (lock.current || (prestation && !isDirty)) return;
    lock.current = true; setSubmitError("");
    try { if (await onSubmit(data) !== false) reset(data); }
    catch (failure) { setSubmitError(failure?.response?.data?.detail || "Impossible d'enregistrer la prestation. Vos modifications sont conservées."); }
    finally { lock.current = false; }
  };

  return (
    <Dialog footer={<>
            <button type="button" onClick={close} disabled={isSubmitting} className="btn-secondary">
              Annuler
            </button>
            <button form={formId} type="submit" disabled={isSubmitting || (prestation && !isDirty)} className="btn-primary">
              {isSubmitting ? "Enregistrement..." : prestation ? "Mettre à jour" : "Créer la prestation"}
            </button>
           </>} title={prestation ? "Modifier la prestation" : "Nouvelle prestation"} onClose={close} busy={isSubmitting} size="max-w-xl">
        <form id={formId}
          onSubmit={handleSubmit(handleFormSubmit)}
          className="space-y-6"
        >
          <fieldset disabled={isSubmitting} className="space-y-6">
          {/* Description */}
          <div>
            <label htmlFor="prestation-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
                <input aria-invalid={!!errors.description} aria-describedby={errors.description ? "error-prestationform-description" : undefined} id="prestation-description"
              type="text"
              className="input-field"
              placeholder="Ex: Développement d'application web"
              {...register("description", {
                required: "La description est requise",
              })}
            />
            {errors.description && (
              <p id="error-prestationform-description" className="text-red-600 text-sm mt-1">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Unité et quantité */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="prestation-unite" className="block text-sm font-medium text-gray-700 mb-1">
                Unité *
              </label>
                <select aria-invalid={!!errors.unite} aria-describedby={errors.unite ? "error-prestationform-unite" : undefined} id="prestation-unite"
                className="input-field"
                {...register("unite", { required: "L'unité est requise" })}
              >
                <option value="heure">Heure</option>
                <option value="jour">Jour</option>
                <option value="unite">Unité</option>
                <option value="projet">Projet</option>
              </select>
              {errors.unite && (
                <p id="error-prestationform-unite" className="text-red-600 text-sm mt-1">
                  {errors.unite.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="prestation-quantite" className="block text-sm font-medium text-gray-700 mb-1">
                Quantité par défaut *
              </label>
                <input aria-invalid={!!errors.quantite} aria-describedby={errors.quantite ? "error-prestationform-quantite" : undefined} id="prestation-quantite"
                type="number"
                step="0.01"
                min="0"
                className="input-field"
                {...register("quantite", { valueAsNumber: true,
                  required: "La quantité est requise",
                  min: { value: 0, message: "La quantité doit être positive" },
                })}
              />
              {errors.quantite && (
                <p id="error-prestationform-quantite" className="text-red-600 text-sm mt-1">
                  {errors.quantite.message}
                </p>
              )}
            </div>
          </div>

          {/* Prix unitaire et devise */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="prestation-prix_unitaire" className="block text-sm font-medium text-gray-700 mb-1">
                Prix unitaire *
              </label>
                <input aria-invalid={!!errors.prix_unitaire} aria-describedby={errors.prix_unitaire ? "error-prestationform-prix_unitaire" : undefined} id="prestation-prix_unitaire"
                type="number"
                step="0.01"
                min="0"
                className="input-field"
                {...register("prix_unitaire", { valueAsNumber: true,
                  required: "Le prix unitaire est requis",
                  min: { value: 0, message: "Le prix doit être positif" },
                })}
              />
              {errors.prix_unitaire && (
                <p id="error-prestationform-prix_unitaire" className="text-red-600 text-sm mt-1">
                  {errors.prix_unitaire.message}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="prestation-devise" className="block text-sm font-medium text-gray-700 mb-1">
                Devise *
              </label>
                <select aria-invalid={!!errors.devise} aria-describedby={errors.devise ? "error-prestationform-devise" : undefined} id="prestation-devise"
                className="input-field"
                {...register("devise", { required: "La devise est requise" })}
                defaultValue="EUR"
              >
                <option value="EUR">EUR</option>
                <option value="CAD">CAD</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
              </select>
              {errors.devise && (
                <p id="error-prestationform-devise" className="text-red-600 text-sm mt-1">
                  {errors.devise.message}
                </p>
              )}
            </div>
          </div>

          {/* Aperçu du total */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span className="font-medium text-gray-700">Total :</span>
              <span className="text-lg font-bold text-primary-600">
                {formatMoney(watchedQuantite * watchedPrixUnitaire, watch("devise", "EUR"))}
              </span>
            </div>
          </div>

          {submitError && <p role="alert" className="text-sm text-red-700">{submitError}</p>}

          </fieldset>
        </form>
    </Dialog>
  );
};

export default PrestationForm;
